import { z } from 'zod';

// ─── User Management ────────────────────────────────────────────────
export const UserRoleSchema = z.enum(['admin', 'editor', 'viewer']);
export const UserStatusSchema = z.enum(['active', 'inactive', 'suspended']);

export const AdminUserSchema = z.object({
	id: z.string(),
	email: z.string().email(),
	name: z.string(),
	role: UserRoleSchema,
	status: UserStatusSchema,
	permissions: z.array(z.string()),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const CreateUserInputSchema = z.object({
	email: z.string().email(),
	name: z.string().min(1),
	role: UserRoleSchema,
	permissions: z.array(z.string()).optional(),
});

export const UpdateUserInputSchema = z.object({
	name: z.string().optional(),
	role: UserRoleSchema.optional(),
	status: UserStatusSchema.optional(),
	permissions: z.array(z.string()).optional(),
});

// ─── Feature Flags ──────────────────────────────────────────────────
export const FlagEnvironmentSchema = z.enum(['production', 'staging', 'local']);

const FlagNameSchema = z
	.string()
	.min(1)
	.regex(/^[a-z0-9][a-z0-9-_]*$/, 'Flag names must use lowercase letters, numbers, hyphens, or underscores');

const FlagPercentageSchema = z.number().min(0).max(100);

export const AdminFlagSchema = z.object({
	name: FlagNameSchema,
	description: z.string(),
	enabled: z.boolean(),
	percentage: FlagPercentageSchema,
	environments: z.array(FlagEnvironmentSchema),
	allowList: z.array(z.string()),
	updatedAt: z.string(),
});

export const CreateFlagInputSchema = z.object({
	name: FlagNameSchema,
	description: z.string(),
	enabled: z.boolean().optional(),
	percentage: FlagPercentageSchema.optional(),
	environments: z.array(FlagEnvironmentSchema).optional(),
});

export const UpdateFlagInputSchema = z.object({
	description: z.string().optional(),
	enabled: z.boolean().optional(),
	percentage: FlagPercentageSchema.optional(),
	environments: z.array(FlagEnvironmentSchema).optional(),
	allowList: z.array(z.string()).optional(),
});

// ─── Translations ───────────────────────────────────────────────────
export const LocaleSummarySchema = z.object({
	locale: z.string(),
	keyCount: z.number(),
	updatedAt: z.string(),
});

export const TranslationBundleSchema = z.object({
	locale: z.string(),
	keys: z.record(z.unknown()),
	updatedAt: z.string(),
});

// ─── Settings ───────────────────────────────────────────────────────
export const SettingsSectionSchema = z.object({
	id: z.string(),
	title: z.string(),
	values: z.record(z.union([z.string(), z.boolean(), z.number()])),
});

// ─── Dashboard ──────────────────────────────────────────────────────
export const ActivityEntrySchema = z.object({
	action: z.string(),
	detail: z.string(),
	timestamp: z.string(),
});

export const DashboardStatsSchema = z.object({
	users: z.object({ total: z.number(), active: z.number(), newThisMonth: z.number() }),
	flags: z.object({ total: z.number(), enabled: z.number() }),
	translations: z.object({ locales: z.number(), totalKeys: z.number() }),
	recentActivity: z.array(ActivityEntrySchema),
});
